import React, { useCallback, useState, useMemo } from "react";
import { useMutation, useQueryClient } from "react-query";
import { TASKS_QUERY_FILTER } from "ui-io/queries/tasks/task-query";
import { Box, Text, ThemedDialog } from "ui-components";
import { delay } from "ui-lib";
import { TaskT } from "ui-io/task-io/task-t";
import {
  taskBulkEventsMutation,
  TaskEventMutationErrorIO,
  TaskEventMutationSuccessIO,
} from "ui-io/queries/tasks/task-event-mutation";
import {
  Button,
  Classes,
  FormGroup,
  IconName,
  InputGroup,
  Label,
  Menu,
  MenuItem,
  Position,
  Tag,
  TextArea,
} from "@blueprintjs/core";
import { BulkActionType, BulkEventType, TaskBulkActionIO } from "ui-io/task-io";
import { match } from "ts-pattern";
import useSession from "ui-io/use-session";
import { Popover2, Tooltip2 } from "@blueprintjs/popover2";
import {
  taskBlockMutation,
  taskUnblockMutation,
} from "ui-io/queries/tasks/task-block-mutation";
import * as t from "io-ts";

import toasts from "../../../utils/toasts";
import SuggestUserInput from "../../suggest-user-input";
import { TASKS_SEARCH_QUERY } from "ui-io/queries/tasks/tasks-query";
const eventIconMap: Record<BulkEventType | BulkActionType, IconName> = {
  PENDING: "following",
  BLOCK: "disable",
  UNBLOCK: "unlock",
  REASSIGN: "following",
  UNASSIGN: "undo",
  START_PUBLICATION: "document-share",
  REJECTED_BY_PUBLISHER: "delete",
};

const getDisplayName = (
  selectedEvent: BulkEventType | BulkActionType | undefined
) => {
  const bulkActionDisplayName: Partial<
    Record<BulkEventType | BulkActionType, string>
  > = {
    REJECTED_BY_PUBLISHER: "REJECT",
  };
  return !selectedEvent
    ? selectedEvent
    : (bulkActionDisplayName[selectedEvent] ?? selectedEvent);
};
const AdminPopoverContent = ({
  tasks,
  selectedEvent,
  eventAssignee,
  setEventAssignee,
  onSendAdminEvent,
  onSendBulkAction,
  closePopup,
}: {
  tasks: TaskT[];
  allUserList?: string[];
  selectedEvent?: BulkEventType | BulkActionType;
  eventAssignee: string;
  setEventAssignee: (a: string) => void;
  closePopup: () => void;
  onSendAdminEvent: (additionalFields?: {
    radar: string;
    comment: string;
  }) => void;
  onSendBulkAction: (additionalFields?: {
    radar: string;
    comment: string;
  }) => void;
}) => {
  const [additionalFields, setAdditionalFields] = useState({
    radar: "",
    comment: "",
  });

  const handleOnAdditionalFieldChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setAdditionalFields({
      ...additionalFields,
      [event.target.name]: event.target.value,
    });
  };
  if (!selectedEvent) {
    return null;
  }

  return (
    <div className={Classes.DIALOG_BODY}>
      <Box mb={1}>
        <Text>
          Are you sure you want to <Tag>{getDisplayName(selectedEvent)}</Tag>{" "}
          {tasks.length} task(s)?
        </Text>
      </Box>

      {selectedEvent === "REASSIGN" && (
        <FormGroup
          helperText="Enter username of new assignee"
          labelInfo="(required)"
          inline
        >
          <SuggestUserInput
            username={eventAssignee}
            setUsername={setEventAssignee}
          />
        </FormGroup>
      )}
      <ul>
        {tasks.map(({ id, title }) => (
          <li key={id}>
            <Text muted inline>
              {/* {id.slice(id.length - 5)} */}
              {id}
            </Text>{" "}
            {title}
          </li>
        ))}
      </ul>
      {selectedEvent === "BLOCK" && (
        <>
          <Box mt={1}>
            <Label>Radar</Label>
            <InputGroup
              value={additionalFields.radar}
              name="radar"
              onChange={handleOnAdditionalFieldChange}
              placeholder="Radar Id"
            />
          </Box>
          <Box mt={1}>
            <Label>Comment</Label>
            <TextArea
              fill
              name="comment"
              value={additionalFields.comment}
              rows={2}
              onChange={handleOnAdditionalFieldChange}
              placeholder="Write"
            />
          </Box>
        </>
      )}
      <Box flexDir={"row-reverse"} mt={"medium"} gap={"small"}>
        <Button
          intent={"success"}
          onClick={() => {
            selectedEvent === "BLOCK"
              ? onSendBulkAction(additionalFields)
              : onSendAdminEvent();
          }}
          icon="tick-circle"
        >
          Confirm
        </Button>
        <Button intent={"danger"} icon="delete" onClick={closePopup}>
          Cancel
        </Button>
      </Box>
    </div>
  );
};

const BulkActionMenu = ({
  bulkEvents,
  setSelectedEvent,
}: {
  bulkEvents: (BulkEventType | BulkActionType)[];
  setSelectedEvent: (
    bulkEvent: BulkEventType | BulkActionType | undefined
  ) => void;
}) => {
  return (
    <Menu>
      {bulkEvents.length ? (
        bulkEvents.map((item) => (
          <MenuItem
            key={item}
            onClick={() => {
              setSelectedEvent(item);
            }}
            text={getDisplayName(item)}
          />
        ))
      ) : (
        <MenuItem text="Select a valid task" disabled />
      )}
    </Menu>
  );
};

const canPerformActionOnTask = (
  action: BulkEventType | BulkActionType,
  task: TaskT,
  username: string
): boolean =>
  match(action)
    .with(
      "BLOCK",
      () => false /* hide all block actions for all tasks for now */
    )
    .with("PENDING", () => task.isPending)
    .with("UNBLOCK", () => task.isBlocked)
    .with("REASSIGN", () => task.isReassignable)
    .with("UNASSIGN", () => task.isAssigned && task.isInProgress)
    .with("START_PUBLICATION", () => task.isReadyForPublish)
    .with(
      "REJECTED_BY_PUBLISHER",
      () => task.isReadyForPublish && task.assignee === username
    )
    .exhaustive();

const BulkActionsDropdown = ({
  disabled: forceDisabled,
  tasks,
  actions,
  allUserList,
}: {
  disabled?: boolean;
  tasks: TaskT[];
  actions: (BulkEventType | BulkActionType)[];
  allUserList?: string[];
}) => {
  const queryClient = useQueryClient();
  const [selectedEvent, setSelectedEvent] = useState<
    BulkEventType | BulkActionType | undefined
  >(undefined);
  const { session } = useSession();
  const username = session?.username;
  const [eventAssignee, setEventAssignee] = useState("");
  const adminBulkEventMutation = useMutation(taskBulkEventsMutation, {
    async onError(error, { eventType }) {
      console.error(error);
      const errorMessage = `Encountered an error when attempting to submit event "${eventType}" for ${tasks.length} task(s)`;
      await delay(500);

      queryClient.refetchQueries([TASKS_QUERY_FILTER]);
      queryClient.refetchQueries([TASKS_SEARCH_QUERY]);
      toasts.error(errorMessage);
    },
    async onSuccess({ tasks }, { eventType }) {
      const successful = tasks
        .filter(TaskEventMutationSuccessIO.is)
        .map((task: { task_id: string }) => task.task_id);
      const failed = Array.from(
        new Set(
          tasks
            .filter(TaskEventMutationErrorIO.is)
            .map(({ errors }: { errors: any }) => errors.join(", "))
        )
      );

      if (successful.length > 0) {
        toasts.success(
          <>
            Successfully submitted bulk event "{eventType}" for task(s)
            <ul>
              {successful.map((id: any) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          </>
        );
      }

      if (failed.length > 0) {
        toasts.error(
          <>
            Failed to submit bulk event "{eventType}" for {failed.length}{" "}
            task(s) due to:
            <ul>
              {failed.map((msg, index) => (
                <li key={index}>{String(msg)}</li>
              ))}
            </ul>
          </>
        );
      }
      await delay(500);

      queryClient.refetchQueries([TASKS_QUERY_FILTER]);
      queryClient.refetchQueries([TASKS_SEARCH_QUERY]);
    },
  });
  const blockMutation = useMutation(taskBlockMutation, {
    onSuccess: async (result, { taskIds }) => {
      // tyyyypeeeeeescrrrriiiiiiiipt
      const seed: { successes: any[]; failures: any[] } = {
        successes: [],
        failures: [],
      };
      const { failures, successes } = result.reduce(
        (agg: any, next: any, index: number) => {
          if (t.type({ failure: t.string }).is(next)) {
            return {
              ...agg,
              failures: [
                ...agg.failures,
                {
                  ...next,
                  // @ts-ignore
                  task_id: taskIds[index]!,
                },
              ],
            };
          }

          return {
            ...agg,
            successes: [...agg.successes, next],
          };
        },
        seed
      );

      if (failures.length > 0) {
        toasts.error(`Failed to block ${failures.length} tasks`);
        console.error(`Failed to block ${failures.length} tasks`, failures);
      }

      if (successes.length > 0) {
        toasts.success(`Successfully blocked ${successes.length} tasks`);
        console.info(
          `Successfully blocked ${successes.length} tasks`,
          successes
        );
      }
      await delay(500);

      queryClient.refetchQueries([TASKS_QUERY_FILTER]);
    },
    async onError(error) {
      console.error(error);
      const errorMessage = `Encountered an error when attempting to block ${tasks.length} task(s)`;
      await delay(500);

      queryClient.refetchQueries([TASKS_QUERY_FILTER]);
      toasts.error(errorMessage);
    },
  });

  const unblockMutation = useMutation(taskUnblockMutation, {
    onSuccess: async (result, { taskIds }) => {
      // tyyyypeeeeeescrrrriiiiiiiipt
      const seed: { successes: any[]; failures: any[] } = {
        successes: [],
        failures: [],
      };
      const { failures, successes } = result.reduce(
        (agg: any, next: any, index: number) => {
          if (t.type({ failure: t.string }).is(next)) {
            return {
              ...agg,
              failures: [
                ...agg.failures,
                {
                  ...next,
                  // @ts-ignore
                  task_id: taskIds[index]!,
                },
              ],
            };
          }

          return {
            ...agg,
            successes: [...agg.successes, next],
          };
        },
        seed
      );

      if (failures.length > 0) {
        toasts.error(`Failed to unblock ${failures.length} tasks`);
        console.error(`Failed to unblock ${failures.length} tasks`, failures);
      }

      if (successes.length > 0) {
        toasts.success(`Successfully unblocked ${successes.length} tasks`);
        console.info(
          `Successfully unblocked ${successes.length} tasks`,
          successes
        );
      }
      await delay(500);

      queryClient.refetchQueries([TASKS_QUERY_FILTER]);
    },
    async onError(error) {
      console.error(error);
      const errorMessage = `Encountered an error when attempting to unblock ${tasks.length} task(s)`;
      await delay(500);

      queryClient.refetchQueries([TASKS_QUERY_FILTER]);
      toasts.error(errorMessage);
    },
  });

  const filteredActions: (BulkEventType | BulkActionType)[] = useMemo(
    () =>
      forceDisabled || tasks.length < 1
        ? []
        : actions
            .filter((action) =>
              tasks.every((task) =>
                canPerformActionOnTask(action, task, username ?? "")
              )
            )
            .map((action) =>
              action == "PENDING" || action == "UNASSIGN" ? "REASSIGN" : action
            ),
    [forceDisabled, tasks, actions, username]
  );

  const isDisabled =
    filteredActions.length < 1 || adminBulkEventMutation.isLoading;

  const onSendAdminEvent = useCallback(async () => {
    if (!selectedEvent || isDisabled || TaskBulkActionIO.is(selectedEvent)) {
      return;
    }

    setSelectedEvent(undefined);
    setEventAssignee("");

    const payload = {
      taskIds: tasks.map(({ id }) => id),
      eventType: selectedEvent,
      user: "",
      updatedBy: username,
    };

    if (selectedEvent === "REASSIGN") payload["user"] = eventAssignee;
    adminBulkEventMutation.mutate(payload);
  }, [eventAssignee, isDisabled, selectedEvent, tasks, adminBulkEventMutation]);

  const onSendBulkAction = useCallback(
    async (additionalFields?: { radar: string; comment: string }) => {
      if (!selectedEvent || isDisabled || !TaskBulkActionIO.is(selectedEvent)) {
        return;
      }
      setSelectedEvent(undefined);
      setEventAssignee("");

      if (selectedEvent === "BLOCK") {
        const { radar, comment } = additionalFields ?? {};

        blockMutation.mutate({
          taskIds: tasks.map(({ id }) => id),
          radar,
          comment,
        });
        return;
      }

      if (selectedEvent === "UNBLOCK") {
        unblockMutation.mutate({
          taskIds: tasks.map(({ id }) => id),
        });
        return;
      }
    },
    [selectedEvent, isDisabled, blockMutation, tasks, unblockMutation]
  );

  return (
    <>
      <Popover2
        position="bottom-left"
        content={
          <BulkActionMenu
            bulkEvents={filteredActions}
            setSelectedEvent={setSelectedEvent}
          />
        }
      >
        <Tooltip2
          content="Bulk Actions"
          position={Position.LEFT}
          hoverOpenDelay={1000}
        >
          <Button
            intent={isDisabled ? "none" : "primary"}
            icon="pivot"
            loading={adminBulkEventMutation.isLoading}
          />
        </Tooltip2>
      </Popover2>
      <ThemedDialog
        isOpen={!!selectedEvent}
        usePortal
        canEscapeKeyClose
        onClose={() => setSelectedEvent(undefined)}
        icon={selectedEvent ? eventIconMap[selectedEvent] : false}
        title={selectedEvent ? getDisplayName(selectedEvent) : ""}
      >
        <AdminPopoverContent
          tasks={tasks}
          selectedEvent={selectedEvent}
          eventAssignee={eventAssignee}
          setEventAssignee={setEventAssignee}
          onSendAdminEvent={onSendAdminEvent}
          onSendBulkAction={onSendBulkAction}
          allUserList={allUserList}
          closePopup={() => {
            setSelectedEvent(undefined);
          }}
        />
      </ThemedDialog>
    </>
  );
};

export default BulkActionsDropdown;
